import { z } from "zod";

export const enquiryTypes = {
  contact: "General contact",
  partnership: "Partnership proposal",
  brand: "Introduce your brand",
} as const;

export const ecommercePlatforms = [
  "Shopify",
  "WooCommerce",
  "Magento / Adobe Commerce",
  "BigCommerce",
  "Wix",
  "Squarespace",
  "Custom-built",
  "Not launched yet",
  "Other",
] as const;

export const contactProducts = [
  "Buddy AI",
  "Brand Partnerships",
  "Not sure yet",
] as const;

const singleLine = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine(
      (value) => !/[\u0000-\u001f\u007f]/.test(value),
      "Please use a single line",
    );

export const contactSchema = z
  .object({
    kind: z.enum(["contact", "partnership", "brand"]),
    name: singleLine(120).pipe(z.string().min(2, "Enter your name")),
    email: singleLine(254).pipe(
      z.string().email("Enter a valid email address"),
    ),
    company: singleLine(160),
    website: singleLine(500).refine((value) => {
      if (!value) return true;
      try {
        const url = new URL(value);
        return (
          ["https:", "http:"].includes(url.protocol) &&
          !url.username &&
          !url.password
        );
      } catch {
        return false;
      }
    }, "Enter a full website URL, such as https://yourbrand.com"),
    platform: z.union([z.enum(ecommercePlatforms), z.literal("")]),
    otherPlatform: singleLine(120),
    products: z.array(z.enum(contactProducts)).max(contactProducts.length),
    message: z
      .string()
      .trim()
      .min(10, "Please add a little more detail (at least 10 characters)")
      .max(4000),
  })
  .superRefine((data, ctx) => {
    const required = (path: string, message: string) =>
      ctx.addIssue({ code: "custom", path: [path], message });
    if (data.kind !== "contact") {
      if (!data.company)
        required("company", "Enter your company or brand name");
      if (
        !data.website &&
        !(data.kind === "brand" && data.platform === "Not launched yet")
      )
        required("website", "Enter your current website");
      if (!data.products.length)
        required("products", "Choose a product, or “Not sure yet”");
    }
    if (data.kind === "brand") {
      if (!data.platform)
        required("platform", "Choose your ecommerce platform");
      if (data.platform === "Other" && !data.otherPlatform)
        required("otherPlatform", "Enter your platform");
    }
  });

export type ContactFormData = z.infer<typeof contactSchema>;

export const contactSubmissionSchema = z.object({
  enquiry: contactSchema,
  website: z.literal(""),
  token: z.string().min(1).max(2048),
});
